// Cost accordion — sections grouped by cost-driver category, alpha-sorted, with
// a search/add bar (driver suggestions), filter chips (All / One-time /
// Recurring), per-section footer totals (annualised), and a CAPEX / OPEX-COGS /
// OPEX-SGA P&L badge per section via the copied `classifyCostCategory`.

import { useMemo, useState } from 'react';
import { Icon } from '../Icon';
import type {
  PortalCostCreateRequest,
  PortalCostDriver,
  PortalCostItem,
  PortalCostUpdateRequest,
} from '../../api/types';
import { classifyCostCategory } from '../../lib/financial/product-pl';
import { CostDetailPanel } from './CostDetailPanel';
import {
  annualisedCostTotal,
  groupByCategory,
  intervalLabel,
  type FilterChip,
} from './helpers';

interface CostAccordionProps {
  costs: PortalCostItem[];
  drivers: PortalCostDriver[];
  formatCurrency: (value: number | null | undefined) => string;
  readOnly: boolean;
  onCreate: (body: PortalCostCreateRequest) => Promise<void>;
  onUpdate: (costId: string, body: PortalCostUpdateRequest) => Promise<void>;
  onDelete: (costId: string) => Promise<void>;
}

const PL_BADGE: Record<string, { label: string; cls: string }> = {
  CAPEX: { label: 'CAPEX', cls: 'bg-accent-light text-accent' },
  'OPEX-COGS': { label: 'OPEX · COGS', cls: 'bg-success-light text-success' },
  'OPEX-SGA': { label: 'OPEX · SG&A', cls: 'bg-panel text-muted' },
};

type Editing =
  | { mode: 'create'; driver: PortalCostDriver }
  | { mode: 'edit'; driver: PortalCostDriver; item: PortalCostItem }
  | null;

export function CostAccordion({
  costs,
  drivers,
  formatCurrency,
  readOnly,
  onCreate,
  onUpdate,
  onDelete,
}: CostAccordionProps) {
  const [filter, setFilter] = useState<FilterChip>('all');
  const [query, setQuery] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);

  const driverById = useMemo(() => {
    const m = new Map<string, PortalCostDriver>();
    for (const d of drivers) m.set(d.id, d);
    return m;
  }, [drivers]);

  const filtered = useMemo(() => {
    if (filter === 'one_time') return costs.filter((c) => !c.recurring);
    if (filter === 'recurring') return costs.filter((c) => c.recurring);
    return costs;
  }, [costs, filter]);

  const sections = useMemo(
    () => groupByCategory(filtered, (c) => c.cost_driver?.category ?? 'Uncategorised'),
    [filtered],
  );

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return inputFocused ? drivers.slice(0, 8) : [];
    return drivers
      .filter((d) => (d.name ?? '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [drivers, query, inputFocused]);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-sm font-bold text-ink">Costs</div>
        <div className="flex gap-1.5" role="group" aria-label="Filter costs">
          {(['all', 'one_time', 'recurring'] as FilterChip[]).map((chip) => {
            const on = filter === chip;
            const label = chip === 'all' ? 'All' : chip === 'one_time' ? 'One-time' : 'Recurring';
            return (
              <button
                key={chip}
                onClick={() => setFilter(chip)}
                aria-pressed={on}
                className={`rounded-lg border-[1.5px] px-2.5 py-1 text-[0.72rem] font-semibold ${
                  on
                    ? 'border-accent bg-accent-light text-accent'
                    : 'border-line bg-white text-muted hover:border-accent hover:text-accent'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {!readOnly && (
        <div className="relative mb-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setTimeout(() => setInputFocused(false), 150)}
            placeholder="Search cost drivers to add…"
            aria-label="Search cost drivers"
            className="w-full rounded-[9px] border-[1.5px] border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-accent"
          />
          {suggestions.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-[9px] border border-line bg-white shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
              {suggestions.map((d) => (
                <li key={d.id}>
                  <button
                    onClick={() => {
                      setEditing({ mode: 'create', driver: d });
                      setQuery('');
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-panel"
                  >
                    <Icon name="plus" size={11} /> {d.name}
                    <span className="ml-auto text-xs text-muted">{d.category}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {sections.length === 0 ? (
        <div className="rounded-[10px] border border-dashed border-line bg-panel px-3 py-6 text-center text-sm text-muted">
          No costs yet.{!readOnly && ' Search a cost driver above to add one.'}
        </div>
      ) : (
        <div className="space-y-3">
          {sections.map((section) => {
            const pl = classifyCostCategory(section.category);
            const badge = pl ? PL_BADGE[pl] : null;
            return (
              <div key={section.category} className="overflow-hidden rounded-[10px] border border-line">
                <div className="flex items-center justify-between bg-panel px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-ink">{section.category}</span>
                    {badge && (
                      <span className={`rounded-full px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide ${badge.cls}`}>
                        {badge.label}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-semibold text-muted">
                    {formatCurrency(annualisedCostTotal(section.items))}/yr
                  </span>
                </div>
                <ul className="divide-y divide-line">
                  {section.items.map((c) => (
                    <li key={c.id}>
                      <button
                        onClick={() => {
                          const driver = driverById.get(c.costdriver_id ?? '') ?? {
                            id: c.costdriver_id ?? '',
                            name: c.cost_driver?.name ?? null,
                            category: c.cost_driver?.category ?? null,
                            recurring: c.cost_driver?.recurring ?? null,
                            pl_line: null,
                            unit_label: c.cost_driver?.unit_label ?? null,
                          };
                          setEditing({ mode: 'edit', driver, item: c });
                        }}
                        disabled={readOnly}
                        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-panel disabled:cursor-default disabled:hover:bg-white"
                      >
                        <span className="flex items-center gap-2 text-sm text-ink">
                          {c.cost_driver?.name ?? 'Cost'}
                          <span className="text-[0.66rem] uppercase tracking-wide text-muted">
                            {c.recurring ? intervalLabel(c.recurring_interval) : 'One-time'}
                          </span>
                        </span>
                        <span className="text-sm font-semibold text-ink">
                          {formatCurrency(c.cost)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {editing && !readOnly && (
        <div className="mt-3">
          <CostDetailPanel
            driver={editing.driver}
            item={editing.mode === 'edit' ? editing.item : null}
            formatCurrency={formatCurrency}
            onSaveCreate={onCreate}
            onSaveUpdate={onUpdate}
            onDelete={onDelete}
            onClose={() => setEditing(null)}
          />
        </div>
      )}
    </div>
  );
}
